/**
 * IT Admin → Knowledge (9 Oct): pure helpers, kept free of React and path
 * aliases so `node --test` can import them directly.
 *
 * - `lineDiff`: the history's "what changed" between two versions.
 * - `uploadBatches`: split dropped files into requests under the server's
 *   per-request limit (the browser sends base64, a third larger).
 * - `areaOptions`: the area tree as indented choices, retired ones left out.
 * - `listQuery`: the list's filters as query parameters, empties dropped.
 * - `reviewTone` / `statusTone` / `indexTone`: badge colours.
 */

export type DiffKind = "same" | "added" | "removed";

export interface DiffLine {
  kind: DiffKind;
  text: string;
}

/** Line-by-line diff (longest common subsequence), old → new. */
export function lineDiff(before: string, after: string): DiffLine[] {
  const a = before.split("\n");
  const b = after.split("\n");
  // Very large texts: fall back to "all removed, all added" rather than O(n·m) memory.
  if (a.length * b.length > 4_000_000) {
    return [
      ...a.map((text) => ({ kind: "removed" as const, text })),
      ...b.map((text) => ({ kind: "added" as const, text })),
    ];
  }
  const cols = b.length + 1;
  const table = new Uint32Array((a.length + 1) * cols);
  const at = (i: number, j: number): number => table[i * cols + j] ?? 0;
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i * cols + j] =
        a[i] === b[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ kind: "same", text: a[i] ?? "" });
      i += 1;
      j += 1;
    } else if (at(i + 1, j) >= at(i, j + 1)) {
      out.push({ kind: "removed", text: a[i] ?? "" });
      i += 1;
    } else {
      out.push({ kind: "added", text: b[j] ?? "" });
      j += 1;
    }
  }
  while (i < a.length) {
    out.push({ kind: "removed", text: a[i] ?? "" });
    i += 1;
  }
  while (j < b.length) {
    out.push({ kind: "added", text: b[j] ?? "" });
    j += 1;
  }
  return out;
}

/** How many lines a diff adds and removes. */
export function diffCounts(lines: readonly DiffLine[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const line of lines) {
    if (line.kind === "added") {
      added += 1;
    }
    if (line.kind === "removed") {
      removed += 1;
    }
  }
  return { added, removed };
}

export interface SizedFile {
  name: string;
  size: number;
}

/**
 * Group files into upload requests of at most `maxBytes` (raw size) each,
 * in order. A single file over the limit gets a request of its own (the
 * server then names it as too large).
 */
export function uploadBatches<T extends SizedFile>(files: readonly T[], maxBytes: number): T[][] {
  const batches: T[][] = [];
  let current: T[] = [];
  let size = 0;
  for (const file of files) {
    if (current.length > 0 && size + file.size > maxBytes) {
      batches.push(current);
      current = [];
      size = 0;
    }
    current.push(file);
    size += file.size;
  }
  if (current.length > 0) {
    batches.push(current);
  }
  return batches;
}

/** The file types bulk add reads (the server's list). */
export const BULK_EXTENSIONS = [
  ".md",
  ".markdown",
  ".txt",
  ".html",
  ".htm",
  ".docx",
  ".pptx",
  ".pdf",
  ".xlsx",
  ".csv",
  ".zip",
] as const;

/** Whether bulk add can read a file, by its name. */
export function isBulkFile(name: string): boolean {
  const lower = name.toLowerCase();
  return BULK_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export interface AreaLike {
  id: string;
  name: string;
  parent_id?: string | null;
  path: string;
  active: boolean;
}

export interface AreaOption {
  value: string;
  label: string;
  depth: number;
}

/** Areas as choices, parents before children, indented by depth; retired ones left out. */
export function areaOptions(areas: readonly AreaLike[], includeRetired = false): AreaOption[] {
  const byId = new Map(areas.map((area) => [area.id, area]));
  return areas
    .filter((area) => includeRetired || area.active)
    .map((area) => {
      let depth = 0;
      let parent = area.parent_id ? byId.get(area.parent_id) : undefined;
      while (parent !== undefined && depth < 10) {
        depth += 1;
        parent = parent.parent_id ? byId.get(parent.parent_id) : undefined;
      }
      return { value: area.id, label: area.path, depth };
    });
}

/** Area ids → their "Parent › Child" names, unknown ids kept as they are. */
export function areaNames(ids: readonly string[], areas: readonly AreaLike[]): string[] {
  const byId = new Map(areas.map((area) => [area.id, area.path]));
  return ids.map((id) => byId.get(id) ?? id);
}

export interface ListFilters {
  q?: string;
  status?: string;
  tag?: string;
  area?: string;
  owner?: string;
  source_type?: string;
  audience?: string;
  review?: string;
  index?: string;
}

/** The filters with empty values dropped (the query key and the request both use this). */
export function listQuery(filters: ListFilters): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    const text = (value ?? "").trim();
    if (text !== "") {
      out[key] = text;
    }
  }
  return out;
}

export type Tone = "neutral" | "success" | "warning" | "danger" | "info" | "accent";

export function statusTone(status: string): Tone {
  if (status === "published") {
    return "success";
  }
  if (status === "retired") {
    return "neutral";
  }
  return "info";
}

export function reviewTone(state: string): Tone | null {
  if (state === "overdue") {
    return "danger";
  }
  if (state === "due_soon") {
    return "warning";
  }
  return null;
}

export function indexTone(state: string): Tone | null {
  if (state === "failed") {
    return "danger";
  }
  if (state === "pending") {
    return "warning";
  }
  return null;
}

/** "kb/vpn/connect.md" → "connect.md": the file a preview row came from, short. */
export function shortFileName(path: string): string {
  const parts = path.split("/");
  return parts[parts.length - 1] || path;
}

/** Tags typed as "vpn, remote; Outlook" → ["vpn", "remote", "outlook"]. */
export function parseTags(text: string): string[] {
  const seen: string[] = [];
  for (const raw of text.split(/[,;\n]/)) {
    const tag = raw.trim().toLowerCase();
    if (tag !== "" && !seen.includes(tag)) {
      seen.push(tag);
    }
  }
  return seen;
}
